import React, { useContext, useEffect, useState } from "react";
import {
  AddEditUser,
  GetSpecificUserData,
  UploadDocs,
} from "../../utils/api.constant";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Button,
  Typography,
  Form,
  Row,
  Col,
  Input,
  Upload,
  UploadProps,
  GetProp,
} from "antd";
import AuthUtil from "../../utils/auth";
import styles from "./timeline-form.module.css";
import Text from "../../context/language-context";
import useDrawer from "../../hooks/useDrawer";
import { eDocType, eResultCode, eTimeline, eUserType } from "../../utils/enum";
import {
  addEditTimeline,
  getTimeline,
  updateFormStatus,
} from "../../utils/api.constant";
import useFetch from "../../hooks/useFetch";
import { useForm } from "antd/es/form/Form";
import EncryptUtils from "../../utils/encrypt";
import { forIn } from "lodash";
import { LoaderContext } from "../../context/loader";
import { UserContext } from "../../context";
import {
  disableFutureDates,
  disableFutureYears,
} from "../../utils/helperFunctions";
import { env } from "process";
import {
  CheckOutlined,
  LoadingOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { profile } from "console";
import { ToastOpen, ToastType } from "../../state/toast/slice";
import useToast from "../../hooks/useToast";
import { Regex } from "../../utils/validation";

type FileType = Parameters<GetProp<UploadProps, "beforeUpload">>[0];

const ProfileDrawer = () => {
  const { onCloseDrawer } = useDrawer();
  const context = useContext(UserContext);
  const [form] = Form.useForm();
  const { onShowToast } = useToast();
  const { post } = useFetch();

  const [userId, setUserId] = useState<number | null>(null);

  const contextForLoader = useContext(LoaderContext);
  const { isLoading, setIsLoading } = contextForLoader as any;

  const handleSubmit = async (values: {
    fullName: string;
    fatherName: string;
    dateOfBirth: string;
    mobileNumber: number;
    emailAddress: string;
  }) => {
    setIsLoading(true);
    try {
      const requestPayload = {
        data: {
          id: context?.user?.id,
          fullName: values.fullName,
          fatherName: values.fatherName,
          dateOfBirth: values.dateOfBirth,
          mobileNumber: values?.mobileNumber,
          emailAddress: values?.emailAddress,
        },
      };
      const response = await post(AddEditUser, requestPayload);
      const { dataResponse } = response;
      const { returnCode } = dataResponse;
      if (returnCode === eResultCode.SUCCESS) {
        context?.onUpdate?.({
          ...context?.user,
          fullName: values?.fullName,
          fatherName: values?.fatherName,
          dateOfBirth: values?.dateOfBirth,
        });
        onCloseDrawer();
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);

      console.error("Profile Page Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getSpecificUserData = async () => {
    try {
      const requestPayload = {
        data: {},
      };
      const response = await post(GetSpecificUserData, requestPayload);
      const { data, dataResponse } = response;
      const { returnCode, description } = dataResponse;
      if (returnCode === eResultCode.SUCCESS) {
        form.setFieldsValue({
          fullName: data?.fullName,
          fatherName: data?.fatherName,
          dateOfBirth: data?.dateOfBirth,
          emailAddress: data?.emailId.toLowerCase(),
          mobileNumber: data?.mobileNumber,
          aadhaarCardNumber: data?.aadhaarCardNumber,
        });
        setProfileImageFile(data?.userDocs[0]?.docURL);
        setUserId(data?.id);
      } else {
        console.error(`Get Specific User Profile: ${description}`);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (context.user) {
      form.setFieldsValue({
        fullName: context?.user?.fullName,
        fatherName: context?.user?.fatherName,
        dateOfBirth: context?.user?.dateOfBirth,
        emailAddress: context?.user?.emailId,
        mobileNumber: context?.user?.mobileNumber,
        aadhaarCardNumber: context?.user?.aadhaarCardNumber,
      });
    }
    getSpecificUserData();
  }, []);

  const [loading, setLoading] = useState(false);

  // --------- Profile Image ---------
  const [profileImageFile, setProfileImageFile] = useState<File | null>(null);

  const [profileImage, setProfileImage] = useState<string | null>(null);

  const uploadButton = (
    <button style={{ border: 0, background: "none" }} type="button">
      {loading ? <LoadingOutlined /> : <PlusOutlined />}
      <div style={{ marginTop: "var(--bs-item-margin-30)" }}>Upload</div>
    </button>
  );

  const uploadDocument = async (file: File, type: eDocType) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("docType", type.toString());
      formData.append("userId", String(userId));
      const response = await fetch(
        `${process.env.REACT_APP_PUBLIC_API_BASEURL}${UploadDocs}`,
        {
          method: "POST",
          body: formData,
          headers: { ...AuthUtil.getToken() },
        }
      );
      const res = await response.json();
      const { data, dataResponse } = res;
      const { returnCode, description } = dataResponse;

      if (returnCode == eResultCode.SUCCESS) {
        if (type === eDocType.PASSPORTSIZEPHOTO) {
          setProfileImage(data?.docUrl);
        }
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.leftBottom,
          content: Text({
            tid: "PROFILE_UPDATED_SUCCESSFULLY",
            def: "Your profile is updated successfully",
          }),
        });
      } else {
        console.error("Failed to upload images", response.statusText);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (profileImageFile) {
      uploadDocument(profileImageFile, eDocType.PASSPORTSIZEPHOTO);
    }
  }, [profileImageFile]);

  return (
    <div>
      <section className={styles.formHeader}>
        <Typography.Title
          level={3}
          style={{ marginBottom: "0px !important", color: "var(--bs-primary)" }}
        >
          {Text({ tid: "PROFILE", def: "Profile" })}
        </Typography.Title>

        <Button key="reject" variant="outlined" onClick={() => onCloseDrawer()}>
          {Text({ tid: "CLOSE", def: "Close" })}
        </Button>
      </section>
      <div className={styles.formWrapper}>
        <Form layout="vertical" onFinish={handleSubmit} form={form}>
          <Row>
            <Col xs={24} className={styles.profileImageSection}>
              <Form.Item>
                <Upload
                  name="avatar"
                  listType="picture-card"
                  className="profileUpload"
                  showUploadList={false}
                  action={undefined}
                  beforeUpload={() => false}
                  onChange={(e) => {
                    setProfileImageFile(e.file as any);
                  }}
                >
                  {profileImage ? (
                    <img
                      src={`${process.env.REACT_APP_PUBLIC_API_MEDIA_BASEURL}/${profileImage}`}
                      alt="avatar"
                      style={{ width: "100%" }}
                    />
                  ) : (
                    uploadButton
                  )}
                </Upload>
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col xs={24} lg={24}>
              <Form.Item
                name="fullName"
                label={Text({
                  tid: "full_name",
                  def: "Full Name",
                })}
                rules={[
                  {
                    required: true,
                    message: Text({ tid: "REQUIRED", def: "Required" }),
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col xs={24} lg={24}>
              <Form.Item
                name="fatherName"
                label={Text({
                  tid: "fathers_name",
                  def: "Father's name",
                })}
                rules={[
                  {
                    required: true,
                    message: Text({ tid: "REQUIRED", def: "Required" }),
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col xs={24} lg={24}>
              <Form.Item
                name="dateOfBirth"
                label={Text({
                  tid: "date_of_birth",
                  def: "Date of birth",
                })}
                rules={[
                  {
                    required: true,
                    message: Text({ tid: "REQUIRED", def: "Required" }),
                  },
                ]}
              >
                <Input type="date" />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col xs={24} lg={24}>
              <Form.Item
                name="emailAddress"
                label={Text({
                  tid: "email_address",
                  def: "Email address",
                })}
              >
                <Input disabled />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col xs={24} lg={24}>
              <Form.Item
                name="mobileNumber"
                label={Text({
                  tid: "mobile_number",
                  def: "Mobile number",
                })}
                rules={[
                  {
                    required: true,
                    message: Text({ tid: "REQUIRED", def: "Required" }),
                  },
                  {
                    pattern: Regex.MOBILENO, // Updated pattern
                    message: Text({
                      tid: "mobile_number_validation",
                      def: "Enter a valid 10-digit mobile number",
                    }),
                  },
                ]}
              >
                <Input disabled maxLength={10} />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col xs={24} lg={24}>
              <Form.Item
                name="aadhaarCardNumber"
                label={Text({
                  tid: "aadhaar_card_number",
                  def: "Aadhar card number",
                })}
              >
                <Input disabled />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col xs={24} lg={24}>
              <Form.Item className={styles.formFooter}>
                <Button
                  type="primary"
                  htmlType="submit"
                  style={{ float: "right" }}
                >
                  {Text({ tid: "SUBMIT", def: "Submit" })}
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </div>
    </div>
  );
};

export default ProfileDrawer;
